import type { MinifiedTask } from '../../../api/tasks-client/task-client.type';
import { isCustomMeasure } from '../../../api/work-periods-client/work-periods-client.helpers';
import { useWorkPeriodsTasksSingular } from '../../../api/work-periods-client/work-periods-client.hooks';
import type { GetWorkPeriodsMeasurePayload } from '../../../api/work-periods-client/work-periods-client.type';
import { formatToISODate } from '../../../helpers/date-time-helpers';
import { useProcessAnalysisStore } from '../../../store/process-analysis-store/process-analysis-store';
import {
  usePrimaryEntity,
  useTimeAllocation,
  useTimeAllocations,
} from '../../../store/process-analysis-store/process-analysis-store.hooks';
import {
  getEntityType,
  getTimeAllocationEndDate,
} from '../../../helpers/process-analysis-shared/process-analysis-shared.helpers';

/**
 * Custom hook for calculating time period based on selected data point and time allocations
 *
 * @returns {startDate: string | null; endDate: string | null} - The start and end dates
 */
const useTimePeriod = (): { startDate: string | null; endDate: string | null } => {
  const selectedDataPoint = useProcessAnalysisStore((state) => state.selectedDataPoint);
  const timeAllocations = useTimeAllocations();

  if (!selectedDataPoint) {
    return { startDate: null, endDate: null };
  }

  const matchingAllocation = timeAllocations.find((allocation) => allocation.id === selectedDataPoint.rawDate);

  let startDate: string;
  let endDate: string;

  if (matchingAllocation) {
    // Use the time allocation's start and calculated end date (similar to v1 logic)
    startDate = matchingAllocation.start.split('T')[0];
    endDate = getTimeAllocationEndDate(startDate, matchingAllocation.end.split('T')[0]);
  } else {
    // Fallback: use the selected date for both start and end
    const formattedDate = formatToISODate(selectedDataPoint.rawDate);
    startDate = formattedDate;
    endDate = formattedDate;
  }

  return { startDate, endDate };
};

/**
 * Custom hook for generating payload for work units data
 *
 * @returns {GetWorkPeriodsMeasurePayload | null} - The payload for fetching work units data
 */
const useWorkUnitsPayload = (): GetWorkPeriodsMeasurePayload | null => {
  const selectedDataPoint = useProcessAnalysisStore((state) => state.selectedDataPoint);
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const selectedTaskType = useProcessAnalysisStore((state) => state.selectedTaskType);
  const availableMeasures = useProcessAnalysisStore((state) => state.availableMeasures);
  const entity = usePrimaryEntity();
  const timeAllocation = useTimeAllocation();
  const { startDate, endDate } = useTimePeriod();

  if (!selectedDataPoint || !entity || !startDate || !endDate) {
    return null;
  }

  const entityType = getEntityType(activeTab);

  const payload: GetWorkPeriodsMeasurePayload = {
    start_date: startDate,
    end_date: endDate,
    measure_name: [selectedDataPoint.measure],
    time_allocation_type: timeAllocation,
    // Route custom (DSL) measures to /custom-tasks/; the built-in /tasks/ endpoint
    // only knows the WorkPeriodMeasure enum and 500s on a custom config name.
    is_custom: isCustomMeasure(selectedDataPoint.measure, availableMeasures),
  };

  if (entityType === 'portfolio') {
    payload.portfolio_id = entity;
  } else if (entityType === 'project') {
    payload.project_id = entity;
  } else if (entityType === 'subproject') {
    payload.subproject_id = entity;
  }

  if (selectedTaskType) {
    payload.task_type = selectedTaskType;
  }

  return payload;
};

/**
 * Custom hook for fetching work units (tasks) for the selected data point
 *
 * @returns {{ data: MinifiedTask[] | undefined; isFetching: boolean }} - The work units data and a boolean indicating whether the data is being fetched
 */
const useWorkUnitsData = (): { data: MinifiedTask[] | undefined; isFetching: boolean; isLoading: boolean } => {
  const payload = useWorkUnitsPayload();

  const safePayload = payload ?? ({} as GetWorkPeriodsMeasurePayload);

  const { data, query } = useWorkPeriodsTasksSingular({
    payload: safePayload,
    options: {
      enabled: !!payload,
    },
  });

  return {
    data,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
  };
};

export { useWorkUnitsData };
